import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { C } from "./AccountingLayout";
import { toast } from "sonner";
import { X, ArrowUpRight, Receipt, Landmark, Package, Wallet, ScrollText, FileText, ChevronRight } from "lucide-react";

const money = (n) => (n == null ? "—" : `₦${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
const dt = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");

const ICONS = {
  order: Receipt, payment: Wallet, journal: ScrollText, expense: FileText,
  grn: Package, staff_credit: ArrowUpRight, cash_movement: Landmark, shift: Landmark,
};

export default function DrillPanel({ open, onClose, metric, date, locationId, locationName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState(null);   // {type, id} for the second-level drill

  useEffect(() => {
    if (!open || !metric) { setData(null); setSubject(null); return; }
    setLoading(true);
    api.get("/accounting/daily-branch/drill", {
      params: { date, location_id: locationId, metric },
    })
      .then(setData)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, metric, date, locationId]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(59, 39, 34, 0.4)" }} onClick={onClose} data-testid="drill-backdrop" />
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[560px] z-50 shadow-2xl flex flex-col" style={{ background: C.canvas }} data-testid="drill-panel">
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${C.border}`, background: C.panel }}>
          <div>
            <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>{locationName} · {date}</div>
            <div className="font-brand text-xl font-bold" style={{ color: C.text }}>{data?.label || metric}</div>
            {data && <div className="text-sm mt-0.5" style={{ color: C.burgundy }}><b>{money(data.total)}</b> · {data.count} row{data.count === 1 ? "" : "s"}</div>}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#F3ECE2]" data-testid="drill-close"><X size={18} style={{ color: C.text }} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="text-xs text-center py-6" style={{ color: C.softMuted }}>Loading…</div>}
          {!loading && data?.rows?.length === 0 && (
            <div className="text-sm text-center py-8" style={{ color: C.softMuted }}>Nothing contributed to this figure today.</div>
          )}
          <div className="space-y-2">
            {(data?.rows || []).map((r) => {
              const Icon = ICONS[r.type] || Receipt;
              return (
                <button key={r.id} onClick={() => setSubject(r)}
                        className="w-full text-left rounded-2xl border p-3 hover:shadow flex items-center gap-3 transition"
                        style={{ background: C.panel, borderColor: C.border }}
                        data-testid={`drill-row-${r.id}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.borderSoft }}>
                    <Icon size={16} style={{ color: C.burgundy }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm" style={{ color: C.text }}>{r.number || r.id?.slice(0, 8)}</span>
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-bold" style={{ background: C.borderSoft, color: C.softMuted }}>{r.type.replace("_", " ")}</span>
                      {r.reconciled && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-bold" style={{ background: C.greenTint, color: C.green }}>reconciled</span>}
                    </div>
                    <div className="text-xs mt-0.5 truncate" style={{ color: C.softMuted }}>
                      {dt(r.at)}
                      {r.till && <> · {r.till}</>}
                      {r.meta?.employee && <> · {r.meta.employee}</>}
                      {r.meta?.supplier && <> · {r.meta.supplier}</>}
                      {r.meta?.method && <> · {r.meta.method}</>}
                      {r.meta?.memo && <> · {r.meta.memo}</>}
                      {r.meta?.category && <> · {r.meta.category}</>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-brand font-bold text-sm" style={{ color: C.text }}>{money(r.amount)}</div>
                    <ChevronRight size={14} style={{ color: C.softMuted, marginTop: 2 }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {subject && <SubjectPanel subject={subject} onClose={() => setSubject(null)} />}
    </>
  );
}


function SubjectPanel({ subject, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const oid = subject.order_id || subject.id;
    const call =
      subject.type === "journal" ? api.get(`/accounting/journals/${subject.id}`)
      : (subject.type === "order" || subject.type === "payment") ? api.get(`/accounting/orders/${oid}/detail`)
      : Promise.resolve(subject.meta || {});
    call.then(setDetail).catch(() => setDetail(subject.meta || {})).finally(() => setLoading(false));
  }, [subject]);

  return (
    <>
      <div className="fixed inset-0 z-[55]" style={{ background: "rgba(59, 39, 34, 0.35)" }} onClick={onClose} data-testid="subject-backdrop" />
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[560px] z-[60] shadow-2xl flex flex-col" style={{ background: C.canvas }} data-testid="subject-panel">
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${C.border}`, background: C.panel }}>
          <div>
            <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>{subject.type.replace("_", " ")}</div>
            <div className="font-brand text-lg font-bold" style={{ color: C.text }}>{subject.number || subject.id?.slice(0, 8)}</div>
            <div className="text-xs mt-0.5" style={{ color: C.softMuted }}>{dt(subject.at)}{subject.till ? ` · ${subject.till}` : ""}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#F3ECE2]" data-testid="subject-close"><X size={18} style={{ color: C.text }} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && <div className="text-xs text-center py-6" style={{ color: C.softMuted }}>Loading…</div>}

          {/* Order */}
          {!loading && (subject.type === "order" || subject.type === "payment") && detail && (
            <>
              <Section title="Order">
                <Row label="Total" value={<b>{money(detail.total)}</b>} />
                <Row label="Discount" value={money(detail.discount)} />
                <Row label="VAT" value={money(detail.tax_total || detail.vat)} />
                <Row label="COGS" value={money(detail.cogs_total)} />
                <Row label="Cashier" value={detail.cashier_name || "—"} />
                <Row label="Customer" value={detail.customer_name || "Walk-in"} />
              </Section>
              <Section title={`Items (${(detail.items || []).length})`}>
                {(detail.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0" style={{ borderColor: C.borderSoft, color: C.text }}>
                    <span>{it.quantity} × {it.name}</span>
                    <b>{money((it.unit_price || 0) * (it.quantity || 0))}</b>
                  </div>
                ))}
              </Section>
              <Section title="Payments">
                {(detail.payments || []).map((p, i) => (
                  <Row key={i} label={`${(p.method || "?").toUpperCase()}${p.reference ? ` · ${p.reference}` : ""}`}
                       value={<>{money(p.amount)} {p.reconciled && <span className="ml-1 text-[10px] font-bold" style={{ color: C.green }}>✓</span>}</>} />
                ))}
              </Section>
              {(detail.related_journals || []).length > 0 && (
                <Section title={`Related Journals (${detail.related_journals.length})`}>
                  {detail.related_journals.map((j) => (
                    <Row key={j.id} label={<span style={{ color: C.burgundy, fontWeight: 600 }}>{j.number}</span>} value={j.memo} />
                  ))}
                </Section>
              )}
            </>
          )}

          {/* Journal */}
          {!loading && subject.type === "journal" && detail && (
            <>
              <Section title="Header">
                <Row label="Number" value={<b>{detail.number}</b>} />
                <Row label="Date" value={dt(detail.date)} />
                <Row label="Source" value={`${detail.source} · ${detail.source_event}`} />
                <Row label="Memo" value={detail.memo || "—"} />
                <Row label="Reference" value={detail.reference || "—"} />
              </Section>
              <Section title={`Lines (${(detail.lines || []).length})`}>
                <div className="text-[10px] uppercase tracking-widest grid grid-cols-[1fr_90px_90px] gap-2 pb-1 border-b" style={{ borderColor: C.borderSoft, color: C.softMuted }}>
                  <span>Account</span><span className="text-right">Debit</span><span className="text-right">Credit</span>
                </div>
                {(detail.lines || []).map((l, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px_90px] gap-2 py-1.5 text-xs" style={{ color: C.text }}>
                    <span><b>{l.account_code}</b> · {l.account_name}<div className="text-[10px]" style={{ color: C.softMuted }}>{l.description}</div></span>
                    <span className="text-right font-mono">{l.debit ? money(l.debit) : ""}</span>
                    <span className="text-right font-mono">{l.credit ? money(l.credit) : ""}</span>
                  </div>
                ))}
              </Section>
            </>
          )}

          {/* Generic types */}
          {!loading && !["order", "payment", "journal"].includes(subject.type) && (
            <Section title="Details">
              {Object.entries(subject.meta || {}).map(([k, v]) => v != null && (
                <Row key={k} label={k.replace("_", " ")} value={typeof v === "number" ? money(v) : String(v)} />
              ))}
              <Row label="Amount" value={<b>{money(subject.amount)}</b>} />
            </Section>
          )}
        </div>
      </div>
    </>
  );
}


function Section({ title, children }) {
  return (
    <div className="rounded-2xl border p-3" style={{ background: C.panel, borderColor: C.border }}>
      <div className="text-[10px] tracking-widest uppercase font-semibold mb-2" style={{ color: C.softMuted }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" style={{ borderColor: C.borderSoft, color: C.text }}>
      <span className="capitalize" style={{ color: C.muted }}>{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
